import { supabase } from '../supabaseClient';
import { getActiveTeamId } from '../activeTeam';
import { PLAN_LIMIT_COPY } from '../billing/limits';
import type { PlanLimitHint } from '../billing/limits';

const BUCKET = 'attachments';
const LOGO_BUCKET = 'team-logos';

// Comprueba la cuota/plan ANTES de subir (RPC can_upload). Si no se permite,
// lanza un Error con mensaje de upsell y un `.hint` (file_too_large /
// storage_quota_reached) para que la UI pueda mostrar el banner de mejora.
async function assertCanUpload(file: File): Promise<void> {
  const { data, error } = await supabase.rpc('can_upload', { p_size: file.size });
  if (error) throw error;
  const res = (data ?? {}) as { allowed?: boolean; reason?: string };
  if (res.allowed) return;
  const reason = res.reason as PlanLimitHint | undefined;
  const copy = reason && reason in PLAN_LIMIT_COPY ? PLAN_LIMIT_COPY[reason] : null;
  const msg = copy ? `${copy.title}. ${copy.body}` : 'No se puede subir este archivo.';
  throw Object.assign(new Error(msg), { hint: reason });
}

function safeExt(name = ''): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0) return 'bin';
  return name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8) || 'bin';
}

// Path multi-tenant: el primer segmento es el team_id para que la RLS de
// storage.objects aísle los ficheros por equipo.
function newPath(teamId: string, taskId: string, file: File): string {
  return `${teamId}/messages/${taskId}/${crypto.randomUUID()}.${safeExt(file.name)}`;
}

export async function uploadFile(
  file: File,
  { taskId, teamId }: { taskId: string; teamId: string },
): Promise<{ storagePath: string }> {
  await assertCanUpload(file);
  const path = newPath(teamId, taskId, file);
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    cacheControl: '3600',
  });
  if (error) throw error;
  return { storagePath: path };
}

// Logo del equipo: bucket público, nombre único por subida para evitar
// problemas de caché al reemplazarlo. Devuelve la URL pública estable.
export async function uploadTeamLogo(file: File, teamId: string): Promise<string> {
  const path = `${teamId}/${crypto.randomUUID()}.${safeExt(file.name)}`;
  const { error } = await supabase.storage.from(LOGO_BUCKET).upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;
  return supabase.storage.from(LOGO_BUCKET).getPublicUrl(path).data.publicUrl;
}

// Adjunto de un recordatorio. Mismo bucket, path {teamId}/reminders/{id}/...
// (la RLS de storage lo aísla por equipo; la tabla reminder_attachments, por
// usuario). Devuelve el storage_path para guardarlo en la tabla.
export async function uploadReminderFile(
  file: File,
  reminderId: string,
): Promise<{ storagePath: string }> {
  const teamId = getActiveTeamId();
  if (!teamId) throw new Error('No hay espacio de trabajo activo');
  await assertCanUpload(file);
  const path = `${teamId}/reminders/${reminderId}/${crypto.randomUUID()}.${safeExt(file.name)}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    cacheControl: '3600',
  });
  if (error) throw error;
  return { storagePath: path };
}

export async function removeStorageFile(storagePath: string): Promise<void> {
  await supabase.storage.from(BUCKET).remove([storagePath]);
}

export async function getSignedUrl(
  storagePath: string,
  expiresInSec = 3600,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, expiresInSec);
  if (error) throw error;
  return data.signedUrl;
}

export async function getSignedUrls(
  paths: string[],
  expiresInSec = 3600,
): Promise<Record<string, string>> {
  if (!paths || paths.length === 0) return {};
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(paths, expiresInSec);
  if (error) throw error;
  const entries: Array<[string, string]> = [];
  for (const d of data) {
    const path = d.path;
    const signedUrl = d.signedUrl;
    if (!path || !signedUrl) continue;
    entries.push([path, signedUrl]);
  }
  return Object.fromEntries(entries);
}

export function humanSize(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  const unit = units[i] ?? 'B';
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${unit}`;
}
